import React, { useCallback, useState } from 'react';

import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Grid,
  Typography,
  LinearProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '@material-ui/lab';
import { Box, IconButton, InputAdornment } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { COLOR_PRIMARY, DARK_GREY } from 'src/utils/const';
import { callApi } from 'src/core/callApi';
import Copyright from './Copyright';
import { RegisterPropTypes } from 'src/interfaces/iproptypes';
import { getMarketingTrackers } from 'src/utils/functions';
import { timeZoneCCMap } from 'src/App/assets/timeZoneCCMap';

import { GoogleAuth } from '../GoogleAuth';

import AirbticsLogoFull from 'src/App/assets/logoMobile.jpg';
import { useEmailValidation } from 'src/hooks/useCustomHooks';
import { useSelector } from 'react-redux';
import RateLimitAlert from 'src/App/Dashboards/Market/Components/Alerts/RateLimitAlert';
import { completePostSignupNavigation } from './utils/postSignupRedirect';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100vh',
    overflowY: 'auto',
  },
  image: {
    backgroundImage: 'url(https://airbtics-resource.s3.us-east-2.amazonaws.com/quote.png)',
    backgroundRepeat: 'no-repeat',
    backgroundColor:
      theme.palette.type === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxHeight: '100vh',
    position: 'relative',
    paddingTop: '4px',
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
    backgroundColor: COLOR_PRIMARY,
    '&:hover': {
      backgroundColor: COLOR_PRIMARY,
    },
  },
  progressBar: {
    width: '100%',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 1000,
  },
  formControl: {
    marginTop: 15,
  },
  logo: {
    width: '150px',
    marginBottom: '20px',
  },
  signupText: {
    marginTop: '10px',
    marginBottom: '-10px',
    fontSize: '20px',
    fontWeight: 'bolder',
    color: '#7c7c7c',
  },
  menuItem: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: '1.2',
    padding: '12px 16px',
  },
  select: {
    '& .MuiSelect-select': {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      lineHeight: '1.2',
    },
  },
}));

const setMarketingTrackingURL =
  'https://4b5jgzfm05.execute-api.us-east-2.amazonaws.com/default/setMarketingTracking';

const Login = ({
  registerThunk,
  getTokenThunk,
  googleAuthRegisterThunk,
  loading,
  error,
  errorReason,
  redirectToLoginPage,
  getUserInfo,
  landing,
  device,
}: RegisterPropTypes) => {
  const [lang, page, type, region] = getMarketingTrackers();
  const classes = useStyles();
  const isLoggedIn = Boolean(getTokenThunk());

  const { email, emailError, emailErrorMessage, handleEmailChange } = useEmailValidation();

  const [emptyEmailError, setEmptyEmailError] = useState<string>('');
  const [password, changePassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [name, changeName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const signupCountryCode = timeZoneCCMap[userTimeZone];
  const tailoredRegion = window.localStorage.getItem('tailoredRegion');
  const affiliateKey = window.localStorage.getItem('affiliatedKey');

  const isRateLimited = useSelector((state: any) => state.auth.isRateLimited);

  let urlToRedirect = window.location.pathname;

  if (
    urlToRedirect.includes(
      '/airbnb-data/united-kingdom/0/the%20city%20of%20brighton%20and%20hove'
    ) ||
    urlToRedirect.includes('/discovery')
  ) {
    urlToRedirect = '/airbnb-data';
  }

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      changePassword(event.target.value);
    },
    []
  );

  const handleNameChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      changeName(event.target.value);
    },
    []
  );

  const handleRegister = useCallback(
    async event => {
      event.preventDefault();

      // Reset validation errors
      setNameError('');
      setPasswordError('');
      setEmptyEmailError('');

      let hasError = false;

      // Validate required fields
      if (!email) {
        setEmptyEmailError('Please enter your email');
        hasError = true;
      }

      if (!name) {
        setNameError('Please enter your name');
        hasError = true;
      }

      if (!password) {
        setPasswordError('Please enter a password');
        hasError = true;
      }

      if (hasError) {
        return;
      }

      const search_attempt_history = localStorage.getItem('investment-search-attempts');
      const noOfUserSearch = search_attempt_history ? parseInt(search_attempt_history) : 0;

      const successfullRegistration = await registerThunk({
        email,
        password,
        name,
        userDescription: '',
        userSubDescription: '',
        lang,
        page,
        type,
        region,
        landing,
        device,
        signupCountryCode,
        tailoredRegion,
        affiliateKey,
        noOfUserSearch,
      });

      if (successfullRegistration) {
        callApi(
          setMarketingTrackingURL,
          {
            queryParams: {
              user_signed_up: true,
            },
          },
          false
        );
        localStorage.removeItem('marketing-trackers');
        getUserInfo();

        if (window.location.pathname.includes('/affiliate')) {
          window.location.reload();
          return;
        }

        completePostSignupNavigation();
      }
    },
    [
      email,
      password,
      name,
      lang,
      page,
      type,
      region,
      landing,
      device,
      signupCountryCode,
      tailoredRegion,
      affiliateKey,
      registerThunk,
      urlToRedirect,
    ]
  );

  if (isLoggedIn) {
    window.localStorage.setItem('user-name', name);
    window.localStorage.setItem('user-email', email);
  }

  return (
    <>
      {loading && <LinearProgress className={classes.progressBar} />}      
      <CssBaseline />
      <div className={classes.paper}>
        <img src={AirbticsLogoFull} alt="Airbtics Logo" className={classes.logo} />

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          {!error ? (
            <Typography style={{ fontSize: '18px', color: DARK_GREY }}>
              Already have an account?
            </Typography>
          ) : (
            <Alert severity="error">{errorReason}</Alert>
          )}          
          <Button
            variant="contained"
            style={{
              textTransform: 'none',
              backgroundColor: COLOR_PRIMARY,
              color: '#ffff',
              padding: '2px 5px',
            }}
            onClick={redirectToLoginPage}
          >
            <Typography variant="body1">Login</Typography>
          </Button>
        </Box>

        {isRateLimited && <RateLimitAlert />}

        <GoogleAuth landing={landing} device={device} urlToRedirect={urlToRedirect} />

        <Typography component="h1" variant="h5" className={classes.signupText}>
          Sign up
        </Typography>

        <form className={classes.form} noValidate onSubmit={handleRegister}>
          <TextField
            onChange={handleNameChange}
            value={name}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="name"
            label="Your Name"
            name="name"
            autoComplete="name"
            error={!!nameError}
            helperText={nameError}
          />
          <TextField
            onChange={handleEmailChange}
            value={email}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            error={!!emailError || !!emptyEmailError}
            helperText={emailErrorMessage || emptyEmailError}
          />
          <TextField
            onChange={handlePasswordChange}
            value={password}
            variant="outlined"
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            error={!!passwordError}
            helperText={passwordError}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(prev => !prev)}
                  >
                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Typography variant="caption">
            {' '}
            By clicking Sign Up, you agree to our{' '}
            <Link href="https://airbtics.com/privacy-policy/terms-of-service/" target="_blank">
              Terms
            </Link>{' '}
            and that you have read our{' '}
            <Link href="https://airbtics.com/privacy-policy/privacy-policy/" target="_blank">
              Privacy Policy
            </Link>
            .{' '}
          </Typography>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
            disabled={loading}
          >
            Sign Up
          </Button>
          <Grid container>
            <Grid item xs>
              <Link href="/reset-password" variant="body2">
                Forgot password?
              </Link>
            </Grid>
            <Grid item>
              <Copyright />
            </Grid>
          </Grid>
        </form>
      </div>
    </>
  );
};

export default Login;
